import { useState } from 'react';
import classnames from 'classnames'
import { tr } from 'react-hook-form-auto'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  Button,
  Icon,
  Modal,
  ModalAutoform
} from '../../ui/bootstrap/bootstrap'
import { Place, orderGroups, isGroupHidden } from '../../models/place'
import { PlaceIngredientsContainer } from './PlaceIngredientsContainer'

import styles from './PlacesView.sass'

// Dragging sideways is disconcerting: only the vertical component
// matters for sorting
const restrictToVertical = ({ transform }) => ({
  ...transform,
  x: 0
})

const SortableGroupRow = ({ group, hidden, onToggleHidden }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: String(group.id) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const handleToggleHidden = () => onToggleHidden(group)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classnames(styles.orderRow, {
        [styles.dragging]: isDragging,
        [styles.hidden]: hidden
      })}
    >
      <div
        className={styles.handle}
        {...attributes}
        {...listeners}
      >
        <Icon icon="grip-vertical" />
      </div>
      <div className={styles.orderName}>
        {group.name}
      </div>
      <div
        className={styles.visibility}
        onClick={handleToggleHidden}
      >
        <Icon icon={hidden ? 'eye-slash' : 'eye'} />
      </div>
    </div>
  )
}

const PlaceGroupOrder = ({
  place,
  groups,
  onOrderChange,
  onHiddenChange
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 }
    })
  )

  const ordered = orderGroups(place, groups)

  if (ordered.length == 0)
    return null

  const ids = ordered.map(group => String(group.id))

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id == over.id)
      return

    const from = ids.indexOf(active.id)
    const to = ids.indexOf(over.id)

    onOrderChange(place, arrayMove(ids, from, to))
  }

  const handleToggleHidden = group => {
    const id = String(group.id)
    const hidden = (place.hiddenGroups || []).map(String)
    const newHidden = isGroupHidden(place, group) ?
      hidden.filter(other => other != id) :
      [ ...hidden, id ]

    onHiddenChange(place, newHidden)
  }

  return (
    <div className={styles.order}>
      <div className={styles.orderTitle}>
        {tr('models.place.groupOrder')}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[ restrictToVertical ]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={ids}
          strategy={verticalListSortingStrategy}
        >
          {ordered.map(group =>
            <SortableGroupRow
              key={group.id}
              group={group}
              hidden={isGroupHidden(place, group)}
              onToggleHidden={handleToggleHidden}
            />
          )}
        </SortableContext>
      </DndContext>
    </div>
  )
}

const TABS = [
  { id: 'ingredients', label: 'models.place.ingredients' },
  { id: 'groups', label: 'models.place.groups' }
]

const renderTabs = ({ tab, onTabChange }) =>
  <div className={styles.tabs}>
    {
      TABS.map(({ id, label }) =>
        <div
          key={id}
          className={classnames(styles.tab, {
            [styles.tabActive]: tab == id
          })}
          onClick={onTabChange.bind(null, id)}
        >
          {tr(label)}
        </div>
      )
    }
  </div>

const renderPlaces = ({
  collection = [],
  groups,
  openId,
  tab,
  onToggleOpen,
  onTabChange,
  onOrderChange,
  onHiddenChange,
  onEditDoc,
  onDeleteDoc
}) =>
  collection.map(place => {
    const { id, name } = place
    const open = id == openId

    const handleToggle = () => onToggleOpen(id)
    const handleEdit = event => {
      event.stopPropagation()
      onEditDoc({ id, name })
    }
    const handleDelete = event => {
      event.stopPropagation()
      onDeleteDoc({ id, name })
    }

    return (
      <div key={id} className={styles.place}>
        <div
          className={classnames(styles.title, styles.bar)}
          onClick={handleToggle}
        >
          <Icon icon={open ? 'chevron-up' : 'chevron-down'} />
          <span className={styles.name}>
            {name}
          </span>
          <div className={styles.edit} onClick={handleEdit}>
            <Icon icon="bars" />
          </div>
          <div
            className={classnames(styles.edit, styles.delete)}
            onClick={handleDelete}
          >
            <Icon icon="trash" />
          </div>
        </div>
        {
          open &&
            <div className={styles.panel}>
              {renderTabs({ tab, onTabChange })}
              {
                tab == 'groups' ?
                  <PlaceGroupOrder
                    place={place}
                    groups={groups}
                    onOrderChange={onOrderChange}
                    onHiddenChange={onHiddenChange}
                  /> :
                  <PlaceIngredientsContainer placeId={id} />
              }
            </div>
        }
      </div>
    )
  })

const renderConfirmDelete = ({ doc, onConfirm, onClose }) => {
  const buttons = (
    <div>
      <Button
        text={tr('models.delete')}
        type="danger"
        iconClass="trash"
        onClick={onConfirm}
      />
      <Button
        text={tr('cancel')}
        onClick={onClose}
      />
    </div>
  )

  return (
    <Modal
      title={tr('warning')}
      visible={Boolean(doc)}
      onClose={onClose}
      buttons={buttons}
    >
      {tr('models.place.warnDelete', {
        name: doc && doc.name || ''
      })}
    </Modal>
  )
}

const renderForm = ({ editing, doc, onPlaceUpdate, onClose }) => {
  if (editing) {
    return (
      <ModalAutoform
        onSubmit={onPlaceUpdate}
        initialValues={doc}
        schema={Place.schema}
        form="place"
        onClose={onClose}
        modalTitle={tr('models.places')}
        visible={editing}
      />
    )
  } else
    return null
}

export const PlacesView = ({
  collection,
  groups,
  onPlaceUpdate,
  onPlaceDelete,
  onOrderChange,
  onHiddenChange,
  ...rest
}) => {
  const [ editing, setEditing ] = useState(false)
  const [ editDoc, setEditDoc ] = useState({})
  const [ deleteDoc, setDeleteDoc ] = useState(null)
  const [ openId, setOpenId ] = useState(null)
  const [ tab, setTab ] = useState('ingredients')

  const handleToggleOpen = id => {
    setOpenId(id == openId ? null : id)
  }

  const handleEditDoc = (doc = {}) => {
    setEditing(true)
    setEditDoc(doc)
  }

  const handleModalClose = () => {
    setEditing(false)
  }

  const handleDeleteDoc = doc => {
    setDeleteDoc(doc)
  }

  const handleDeleteClose = () => {
    setDeleteDoc(null)
  }

  const handleDeleteConfirm = () => {
    onPlaceDelete(deleteDoc)
    setDeleteDoc(null)
  }

  const formProps = {
    editing,
    doc: editDoc,
    onPlaceUpdate,
    onClose: handleModalClose
  }

  const renderPlacesProps = {
    collection,
    groups,
    openId,
    tab,
    onToggleOpen: handleToggleOpen,
    onTabChange: setTab,
    onOrderChange,
    onHiddenChange,
    onEditDoc: handleEditDoc,
    onDeleteDoc: handleDeleteDoc
  }

  const confirmDeleteProps = {
    doc: deleteDoc,
    onConfirm: handleDeleteConfirm,
    onClose: handleDeleteClose
  }

  return (
    <div>
      <div className={styles.places}>
        <div key="add" className={styles.place}>
          <a
            className={classnames(styles.add, styles.bar)}
            onClick={handleEditDoc.bind(null, {})}
          >
            <Icon icon="plus" text={tr('add')} />
          </a>
        </div>
        {renderPlaces(renderPlacesProps)}
      </div>
      {renderForm(formProps)}
      {renderConfirmDelete(confirmDeleteProps)}
    </div>
  )
}
